import type { TenantEmailDomainDnsRecord } from "~/gql/graphqlBase.generated";
import type { TenantEmailDomainFragment } from "~/gql/queries/tenantEmailDomain.generated";
import type {
	GetTenantEmailSenderSelectionQuery,
	TenantEmailSenderRowFragment
} from "~/gql/queries/tenantEmailSenders.generated";
import type { TenantSmtpConnectionFragment } from "~/gql/queries/tenantSmtpConnection.generated";

import {
	EmailReplyRouting,
	EmailSenderChannel,
	EmailSenderIntendedUse,
	EmailSenderReadiness,
	EmailSenderSelectionKind,
	EmailSenderVerificationMethod,
	EmailSenderVerificationStatus,
	SmtpConnectionStatus
} from "~/gql/graphqlBase.generated";
import { ownershipChallengeRecords } from "~/helpers/emailDomainPresentation";

export type SenderSetupSelection = GetTenantEmailSenderSelectionQuery["tenantEmailSenderSelection"];

export type SenderMethod = "address" | "domain" | "smtp";

export type SenderMethodState = "failed" | "notSetUp" | "pending" | "verified";

export interface SenderSetupInput {
	domain: TenantEmailDomainFragment | null;
	selection: SenderSetupSelection;
	senders: readonly TenantEmailSenderRowFragment[];
	smtp: TenantSmtpConnectionFragment | null;
}

export interface SenderLine {
	key: string;
	values?: Record<string, string>;
}

export interface TemporarySenderOffer {
	identity: SenderLine;
	reply: SenderLine | null;
}

export interface PendingAddress {
	email: string;
	senderId: string;
}

export interface SenderSetupView {
	dnsRecords: TenantEmailDomainDnsRecord[];
	methods: { method: SenderMethod; state: SenderMethodState }[];
	pendingAddresses: PendingAddress[];
	routeLines: SenderLine[];
	smtpIsVerified: boolean;
	statusKey: string;
	temporaryOffer: TemporarySenderOffer | null;
}

const STATE_RANK: SenderMethodState[] = ["verified", "pending", "failed", "notSetUp"];

const METHOD_OF_CHANNEL: Partial<Record<EmailSenderChannel, SenderMethod>> = {
	[EmailSenderChannel.SendgridDomain]: "domain",
	[EmailSenderChannel.SendgridSingle]: "address",
	[EmailSenderChannel.Smtp]: "smtp"
};

const VERIFICATION_STATE: Record<EmailSenderVerificationStatus, SenderMethodState> = {
	[EmailSenderVerificationStatus.Failed]: "failed",
	[EmailSenderVerificationStatus.Pending]: "pending",
	[EmailSenderVerificationStatus.Verified]: "verified"
};

const SMTP_STATE: Record<SmtpConnectionStatus, SenderMethodState> = {
	[SmtpConnectionStatus.Failed]: "failed",
	[SmtpConnectionStatus.Unverified]: "pending",
	[SmtpConnectionStatus.Verified]: "verified"
};

const getStateLabelKey = (state: SenderMethodState | "temporary"): string => `fernieChat.senderSetup.state.${state}`;

const pickFurthestState = (states: readonly SenderMethodState[]): SenderMethodState =>
	STATE_RANK.find((state) => states.includes(state)) ?? "notSetUp";

const resolveAddressState = (senders: readonly TenantEmailSenderRowFragment[]): SenderMethodState =>
	pickFurthestState(
		senders
			.filter((sender) => sender.verificationMethod === EmailSenderVerificationMethod.SingleSender)
			.map((sender) => VERIFICATION_STATE[sender.verificationStatus])
	);

const formatSenderText = ({ displayName, email }: { displayName: string; email: string }): string =>
	displayName ? `${email} (${displayName})` : email;

const buildRouteLine = (key: string, values?: Record<string, string>): SenderLine =>
	values ? { key: `fernieChat.senderSetup.route.${key}`, values } : { key: `fernieChat.senderSetup.route.${key}` };

const buildSenderLine = ({ effective }: SenderSetupSelection): SenderLine => {
	if (effective.readiness === EmailSenderReadiness.SelectedSenderPending) return buildRouteLine("selectedPending");
	if (effective.readiness === EmailSenderReadiness.SelectedSenderUnavailable)
		return buildRouteLine("selectedUnavailable");
	const { email, isPlatformDefault } = effective.sender;
	if (isPlatformDefault) return buildRouteLine("workSuite", { sender: formatSenderText(effective.sender) });
	return buildRouteLine(`company.${METHOD_OF_CHANNEL[effective.sender.channel] ?? "other"}`, { email });
};

const buildChoiceLine = ({ configured, effective }: SenderSetupSelection): SenderLine | null => {
	switch (configured.kind) {
		case EmailSenderSelectionKind.Platform:
			return effective.intendedUse === EmailSenderIntendedUse.TemporaryWhileCompanySenderPending
				? buildRouteLine("choseWorkSuiteTemporary")
				: buildRouteLine("choseWorkSuite");
		case EmailSenderSelectionKind.Company:
			return configured.companySender
				? buildRouteLine("choseCompany", { email: configured.companySender.email })
				: null;
		case EmailSenderSelectionKind.Inherit:
			return buildRouteLine("noChoice");
		default:
			return null;
	}
};

const buildReplyLine = ({ effective }: SenderSetupSelection): SenderLine | null => {
	if (effective.replyRouting === EmailReplyRouting.PlatformNoReply) return buildRouteLine("replyNoReply");
	if (effective.replyRouting !== EmailReplyRouting.SenderMailbox) return null;
	return effective.replyToEmail
		? buildRouteLine("replyMailbox", { email: effective.replyToEmail })
		: buildRouteLine("replyMailboxNoAddress");
};

const buildTemporaryOffer = (selection: SenderSetupSelection): TemporarySenderOffer =>
	selection.effective.sender.isPlatformDefault
		? {
				identity: {
					key: "fernieChat.senderSetup.temporary.identity",
					values: { sender: formatSenderText(selection.effective.sender) }
				},
				reply: buildReplyLine(selection)
			}
		: { identity: { key: "fernieChat.senderSetup.temporary.identityUnknown" }, reply: null };

const buildSenderSetupView = ({ domain, selection, senders, smtp }: SenderSetupInput): SenderSetupView => {
	const methods = [
		{ method: "address" as const, state: resolveAddressState(senders) },
		{ method: "domain" as const, state: domain ? VERIFICATION_STATE[domain.status] : ("notSetUp" as const) },
		{ method: "smtp" as const, state: smtp ? SMTP_STATE[smtp.status] : ("notSetUp" as const) }
	];
	const hasVerifiedSender =
		senders.some((sender) => sender.verificationStatus === EmailSenderVerificationStatus.Verified) ||
		smtp?.status === SmtpConnectionStatus.Verified;
	const senderState = hasVerifiedSender
		? "verified"
		: pickFurthestState(methods.map((row) => row.state).filter((state) => state !== "verified"));
	const isTemporary = selection.effective.intendedUse === EmailSenderIntendedUse.TemporaryWhileCompanySenderPending;

	return {
		dnsRecords:
			domain && domain.status !== EmailSenderVerificationStatus.Verified
				? [...ownershipChallengeRecords(domain), ...(domain.dnsRecords ?? [])]
				: [],
		methods,
		pendingAddresses: senders
			.filter(
				(sender) =>
					sender.verificationMethod === EmailSenderVerificationMethod.SingleSender &&
					sender.verificationStatus === EmailSenderVerificationStatus.Pending
			)
			.map((sender) => ({ email: sender.email, senderId: sender.senderId })),
		routeLines: [buildSenderLine(selection), buildChoiceLine(selection), buildReplyLine(selection)].filter(
			(line) => line !== null
		),
		smtpIsVerified: smtp?.status === SmtpConnectionStatus.Verified,
		statusKey: getStateLabelKey(isTemporary ? "temporary" : senderState),
		temporaryOffer: isTemporary || hasVerifiedSender ? null : buildTemporaryOffer(selection)
	};
};

const fernieSenderSetup = {
	buildSenderSetupView,
	getStateLabelKey
};

export default fernieSenderSetup;
